import { useState, useEffect, useCallback, useRef } from 'react';
import { jobCompletionEmitter } from '../events/jobCompletion';
import {
  dropUnscoped, pruneForeign, readUserJSON, writeUserJSON,
} from '../utils/perUserStorage';

/* ONE BUCKET PER SIGNED-IN USER. This was a single device-wide key, loaded straight into state
   on mount and never filtered or cleared — so "your video is ready", with the video's TITLE on
   it, was shown to whoever signed in next on the same machine. The uid was already being
   passed to this hook; it just was not used for anything but gating the subscription.
   See utils/perUserStorage.js. */
const NOTIFICATION_STORE = 'magi_notifications';
const MAX_NOTIFICATIONS = 50;
const FOREIGN_TTL = 24 * 60 * 60 * 1000;

export function useNotifications(userId) {
  /* EMPTY UNTIL WE KNOW WHOSE THESE ARE. The initial state used to read storage directly,
     which ran before any uid was known and was therefore unattributable by construction. */
  const [notifications, setNotifications] = useState([]);
  const userIdRef = useRef(userId);
  userIdRef.current = userId;
  // Set once this user's list has been read back, so the persist effect cannot write an empty
  // array over it on the first render.
  const hydratedFor = useRef(null);

  // Load (and re-load whenever the signed-in user changes).
  useEffect(() => {
    setNotifications([]);
    hydratedFor.current = null;
    if (!userId) return;
    // The pre-scoping key belongs to someone unknowable, so it is dropped rather than adopted.
    dropUnscoped(NOTIFICATION_STORE);
    pruneForeign(NOTIFICATION_STORE, userId, FOREIGN_TTL);
    const stored = readUserJSON(NOTIFICATION_STORE, userId, []);
    setNotifications(Array.isArray(stored) ? stored : []);
    hydratedFor.current = userId;
  }, [userId]);

  /* PERSISTED FROM ONE PLACE. Every mutation below used to call `saveNotifications` from
     inside its own state updater — a side effect in a function React is free to call twice.
     The list is the thing being persisted, so persisting it is an effect of it changing. */
  useEffect(() => {
    if (!userId || hydratedFor.current !== userId) return;
    writeUserJSON(NOTIFICATION_STORE, userId, notifications.slice(0, MAX_NOTIFICATIONS));
  }, [notifications, userId]);

  // Listen for job completion events
  useEffect(() => {
    if (!userId) return;

    const unsubscribe = jobCompletionEmitter.on((jobId, videoData) => {
      const notification = {
        id: `${jobId}_${Date.now()}`,
        type: 'video_ready',
        title: videoData?.title || 'Your video is ready',
        jobId,
        videoUrl: videoData?.videoUrl,
        createdAt: new Date().toISOString(),
        read: false,
      };

      setNotifications(prev => [notification, ...prev].slice(0, MAX_NOTIFICATIONS));
    });

    return unsubscribe;
  }, [userId]);

  const unreadCount = notifications.filter(n => !n.read).length;

  const markAsRead = useCallback((notificationId) => {
    setNotifications(prev => prev.map(n =>
      n.id === notificationId ? { ...n, read: true } : n
    ));
  }, []);

  const markAllAsRead = useCallback(() => {
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
  }, []);

  const clearAll = useCallback(() => {
    setNotifications([]);
  }, []);

  return { notifications, unreadCount, markAsRead, markAllAsRead, clearAll };
}
